import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomerAccountSaveRequest } from '../types/customerAccount';

interface CustomerFormFieldsProps {
  value: CustomerAccountSaveRequest;
  disabled?: boolean;
  onChange: (nextValue: CustomerAccountSaveRequest) => void;
  layout?: 'default' | 'compact';
  showActive?: boolean;
}

export const CustomerFormFields: React.FC<CustomerFormFieldsProps> = ({
  value,
  disabled = false,
  onChange,
  layout = 'default',
  showActive = false,
}) => {
  const { t } = useTranslation();
  const compact = layout === 'compact';
  const standardFieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6 };
  const typeFieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 4 };
  const displayNameFieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 8 };
  const streetFieldGrid = compact ? { xs: 12, md: 6 } : { xs: 12 };
  const cityFieldGrid = compact ? { xs: 12, sm: 6, md: 2 } : { xs: 12, sm: 6 };
  const stateFieldGrid = compact ? { xs: 12, sm: 6, md: 2 } : { xs: 12, sm: 6 };
  const postalFieldGrid = compact ? { xs: 12, sm: 6, md: 2 } : { xs: 12, sm: 6 };
  const countryFieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6 };
  const activeFieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6 };

  const updateField = <K extends keyof CustomerAccountSaveRequest>(
    field: K,
    fieldValue: CustomerAccountSaveRequest[K]
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <Grid container spacing={compact ? 1 : 1.5}>
      <Grid item {...typeFieldGrid}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel>{t('billingProfiles.fields.type', 'Type')}</InputLabel>
          <Select
            label={t('billingProfiles.fields.type', 'Type')}
            value={value.type}
            onChange={(event) => updateField('type', event.target.value as CustomerAccountSaveRequest['type'])}
          >
            <MenuItem value="PERSON">{t('billingProfiles.types.person', 'Person')}</MenuItem>
            <MenuItem value="COMPANY">{t('billingProfiles.types.company', 'Company')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item {...displayNameFieldGrid}>
        <TextField
          fullWidth
          size="small"
          required
          disabled={disabled}
          label={t('billingProfiles.fields.displayName', 'Display Name')}
          value={value.displayName}
          onChange={(event) => updateField('displayName', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('billingProfiles.fields.legalName', 'Legal Name')}
          value={value.legalName || ''}
          onChange={(event) => updateField('legalName', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('billingProfiles.fields.contactPerson', 'Contact Person')}
          value={value.contactPersonName || ''}
          onChange={(event) => updateField('contactPersonName', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          type="email"
          label={t('fields.email')}
          value={value.email || ''}
          onChange={(event) => updateField('email', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.phoneNumber')}
          value={value.phoneNumber || ''}
          onChange={(event) => updateField('phoneNumber', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('billingProfiles.fields.taxId', 'Tax ID')}
          value={value.taxId || ''}
          onChange={(event) => updateField('taxId', event.target.value)}
        />
      </Grid>
      <Grid item {...standardFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('billingProfiles.fields.registrationNumber', 'Registration Number')}
          value={value.registrationNumber || ''}
          onChange={(event) => updateField('registrationNumber', event.target.value)}
        />
      </Grid>
      <Grid item {...streetFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.streetAddress')}
          value={value.streetAddress || ''}
          onChange={(event) => updateField('streetAddress', event.target.value)}
        />
      </Grid>
      <Grid item {...cityFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.city')}
          value={value.city || ''}
          onChange={(event) => updateField('city', event.target.value)}
        />
      </Grid>
      <Grid item {...stateFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.state')}
          value={value.state || ''}
          onChange={(event) => updateField('state', event.target.value)}
        />
      </Grid>
      <Grid item {...postalFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.postalCode')}
          value={value.postalCode || ''}
          onChange={(event) => updateField('postalCode', event.target.value)}
        />
      </Grid>
      <Grid item {...countryFieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.country')}
          value={value.country || ''}
          onChange={(event) => updateField('country', event.target.value)}
        />
      </Grid>
      {showActive ? (
        <Grid item {...activeFieldGrid} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value.active)}
                disabled={disabled}
                onChange={(event) => updateField('active', event.target.checked)}
              />
            }
            label={t('common.active', 'Active')}
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          multiline
          minRows={compact ? 1 : 2}
          label={t('fields.notes')}
          value={value.notes || ''}
          onChange={(event) => updateField('notes', event.target.value)}
        />
      </Grid>
    </Grid>
  );
};
